// Per-developer SEO meta + JSON-LD, mirroring usePropertySeo for project
// pages. Sets title / description / OG / Twitter / canonical, emits a
// schema.org Organization block, and a BreadcrumbList trail.

import { DEFAULT_LOCALE } from '~/constants/i18n';
import { useBreadcrumbSchema, useCanonical } from '~/composables/useSchemaOrg';
import { localizePath, normalizeLocale } from '~/utils/i18n/locale';
import { seoText } from '~/utils/i18n/seo';

const SITE_NAME = 'Insiders of Dubai';

type DeveloperLike =
  | {
      id?: number | string;
      name?: string | null;
      slug?: string | null;
      description?: string | null;
      logoUrl?: string | null;
      projectCount?: number | null;
      communities?: string[] | null;
    }
  | null
  | undefined;

const SITE_BASE_DEFAULT = 'https://insidersofdubai.com';

const truncate = (value: string, max = 158): string => {
  if (!value) return '';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
};

export const useDeveloperSeo = (developer: MaybeRefOrGetter<DeveloperLike>): void => {
  const { t, locale } = useI18n();
  const config = useRuntimeConfig();
  const siteBase = computed(() => {
    const fromEnv = (config.public.appUrl as string | undefined)?.trim();
    if (fromEnv) return fromEnv.replace(/\/$/, '');
    return SITE_BASE_DEFAULT;
  });

  const meta = computed(() => {
    const d = toValue(developer);
    if (!d || !d.name) return null;

    const name = d.name;
    const projectCount = Number(d.projectCount ?? 0);
    const top3 = (d.communities ?? []).slice(0, 3).filter(Boolean);
    const focusLine = top3.length
      ? seoText(t('public.seo.developerFocus', { communities: top3.join(', ') }), top3.join(', '))
      : '';
    const description = truncate(
      (d.description && d.description.trim()) ||
        seoText(
          t('public.seo.developerDescription', { name, count: projectCount, focus: focusLine }, projectCount),
          `${name} · ${SITE_NAME}`,
        ),
    );
    const title = seoText(t('public.seo.developerTitle', { name }), `${name} · ${SITE_NAME}`);
    const englishCanonicalPath = d.slug ? `/developers/${d.slug}` : null;
    // Localized page: canonical/og:url point at the current locale's URL (English stays unprefixed).
    const canonicalPath = englishCanonicalPath
      ? localizePath(englishCanonicalPath, DEFAULT_LOCALE, normalizeLocale(locale.value) ?? DEFAULT_LOCALE)
      : null;
    const canonical = canonicalPath ? `${siteBase.value}${canonicalPath}` : siteBase.value;
    const image = d.logoUrl || `${siteBase.value}/og-image.png`;

    const orgLd: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${canonical}#developer`,
      name,
      url: canonical,
      ...(d.logoUrl ? { logo: d.logoUrl, image: d.logoUrl } : {}),
      ...(d.description ? { description: truncate(d.description, 500) } : {}),
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Dubai',
        addressCountry: 'AE',
      },
      ...(projectCount > 0
        ? {
            subjectOf: {
              '@type': 'Dataset',
              name: `${name} project pipeline`,
              description: `Tracked pipeline of ${projectCount} project${projectCount === 1 ? '' : 's'} on Insiders of Dubai.`,
            },
          }
        : {}),
    };

    return {
      title,
      description,
      canonical,
      canonicalPath,
      englishCanonicalPath,
      image,
      orgLd,
    };
  });

  const fallbackTitle = () => seoText(t('public.seo.developerFallbackTitle'), SITE_NAME);

  useSeoMeta({
    title: () => meta.value?.title || fallbackTitle(),
    description: () => meta.value?.description || '',
    ogTitle: () => meta.value?.title || fallbackTitle(),
    ogDescription: () => meta.value?.description || '',
    ogImage: () => meta.value?.image || undefined,
    ogUrl: () => meta.value?.canonical || undefined,
    ogType: 'website',
    ogSiteName: SITE_NAME,
    twitterCard: 'summary_large_image',
    twitterTitle: () => meta.value?.title || fallbackTitle(),
    twitterDescription: () => meta.value?.description || '',
    twitterImage: () => meta.value?.image || undefined,
  });

  useCanonical(() => meta.value?.canonicalPath || null);
  useCanonicalPathDeclaration(() => meta.value?.englishCanonicalPath);

  useHead({
    script: computed(() => {
      const ld = meta.value?.orgLd;
      return ld ? [{ key: 'schema-org-developer', type: 'application/ld+json', innerHTML: JSON.stringify(ld) }] : [];
    }),
  });

  useBreadcrumbSchema(() => {
    const d = toValue(developer);
    if (!d?.name) return [];
    return [
      { name: t('public.developers.home'), url: '/' },
      { name: t('public.developers.title'), url: '/developers' },
      { name: d.name, url: d.slug ? `/developers/${d.slug}` : '/developers' },
    ];
  });
};
